import type { MeResponse, TradingAccount } from '../../api';
import { LOCALE_BY_LANGUAGE } from '../../components/format-money';
import type { SupportedLanguage } from '../../i18n';
import { formatClockTime } from '../shared/format-time';

/** The Telegram session as E11 can tell it from `GET /me` (S8 R19). */
export type TelegramState = 'connected' | 'disconnected' | 'lost' | 'unknown';

/**
 * Reads the Telegram state from the global status (M1 §5.1). M1's precedence (Blocked first)
 * hides the missing steps of a blocked account, so a block without a Telegram cause is `unknown`
 * (Proposal, to be validated; Q113).
 */
export function telegramState(status: MeResponse['status']): TelegramState {
  switch (status.status) {
    case 'blocked':
      return status.causes.some((cause) => cause.reasonCode === 'TELEGRAM_SESSION_LOST')
        ? 'lost'
        : 'unknown';
    case 'incomplete':
      return status.missingSteps.includes('telegram_login') ? 'disconnected' : 'connected';
    case 'operational':
    case 'suspended':
      return 'connected';
  }
}

/**
 * N of the logout sheet (S8 R2, Proposal): the chosen channels, M1's rule (Q44): a channel the
 * trader left (`suspended_external`) does not count.
 */
export function copiedChannelCount(me: MeResponse): number {
  return me.channels.filter((channel) => channel.copyState !== 'suspended_external').length;
}

/** The account summary E11 shows: never the full number (Q81, I9). */
export interface AccountSummary {
  readonly platform: 'MT4' | 'MT5';
  readonly last4: string;
  readonly server: string;
  readonly holder: string;
  readonly currency: string;
  readonly isDemo: boolean;
  /** When the trader authorized orders, or null when no consent is set (`UNREACHABLE` may have none). */
  readonly consentAt: string | null;
}

/** What the trading account section shows (S8 R7 to R10, §4). */
export type AccountView =
  | { readonly kind: 'none' }
  | { readonly kind: 'pending' }
  | { readonly kind: 'connected' | 'unreachable'; readonly summary: AccountSummary };

type ConnectedAccount = Extract<TradingAccount, { status: 'OPERATIONAL' | 'UNREACHABLE' }>;

function summaryOf(account: ConnectedAccount): AccountSummary {
  return {
    platform: account.platform,
    last4: account.accountLast4,
    server: account.server,
    holder: account.holderName,
    currency: account.currency,
    isDemo: account.isDemo,
    consentAt: account.consent?.acceptedAt ?? null,
  };
}

/**
 * Maps `GET /trading-account` to the section's view. `VERIFYING`, `REJECTED` and `VERIFIED` are
 * "not finished": E4 owns them (S6) and an account without consent is not connected (Q83), so no
 * disconnect is offered (Proposal, to be validated).
 */
export function accountView(account: TradingAccount): AccountView {
  switch (account.status) {
    case 'NONE':
      return { kind: 'none' };
    case 'VERIFYING':
    case 'REJECTED':
    case 'VERIFIED':
      return { kind: 'pending' };
    case 'OPERATIONAL':
      return { kind: 'connected', summary: summaryOf(account) };
    case 'UNREACHABLE':
      return { kind: 'unreachable', summary: summaryOf(account) };
  }
}

const DATE_FORMATS = new Map<string, Intl.DateTimeFormat>();

/**
 * The consent instant as `DD/MM/YYYY` and `HH:MM` on 24 h (S8 R7), in `timeZone` (the device's
 * when undefined, as on screen; tests fix it). en-GB and fr-FR both write the date day first.
 */
export function formatConsentInstant(
  isoInstant: string,
  language: SupportedLanguage,
  timeZone?: string,
): { readonly date: string; readonly time: string } {
  const key = `${language}|${timeZone ?? ''}`;
  let format = DATE_FORMATS.get(key);
  if (format === undefined) {
    format = new Intl.DateTimeFormat(LOCALE_BY_LANGUAGE[language], {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      timeZone,
    });
    DATE_FORMATS.set(key, format);
  }
  return {
    date: format.format(new Date(isoInstant)),
    time: formatClockTime(isoInstant, language, { timeZone }),
  };
}
